import type { ResultOf } from '@graphql-typed-document-node/core'
import * as Tabs from '@radix-ui/react-tabs'
// eslint-disable-next-line no-restricted-imports
import { useLocation } from '@remix-run/react'
import { type MouseEvent, useCallback, useMemo } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useIsLoading } from '~/components/filterChips'
import { PlanSearchPrice } from '~/components/plan/PlanSearchPrice.frag'
import { PlanSelectionInfo } from '~/components/plan/PlanSelectionInfo.frag'
import { ConditionsClear } from '~/features/guide/ConditionsClear'
import { useHasCriteria } from '~/features/guide/courses/courseList'
import { useSeatOnlyCourseNavigation } from '~/features/guide/courses/hooks'
import { useSetDefaultMealPeriodTab } from '~/features/guide/mealPeriod'
import {
  MEAL_PERIOD_LABEL,
  NO_INVENTORY_CURRENT_CRITERIA,
  NO_INVENTORY_TEXT,
} from '~/lib/constants/constants'
import { getFragmentData } from '~/lib/graphql'
import type { RestaurantId } from '~/lib/graphql/scalars'
import type { MealPeriodTab } from '~/lib/mealPeriod/tab'
import { mealPeriodTabs, tabIcons } from '~/lib/mealPeriod/tab'
import { groupCoursesByMealPeriodTab, groupSeatOnlyPlansByMealPeriodTab } from '~/lib/plan/plan'
import type { GuideQuery } from '~/lib/query/guide'
import { useIsPC } from '~/lib/ua'
import { useReserveNavigate, useReserveOverlayContext, useSendClickEvent } from '~/navigation'
import { sva } from '~/styled-system/css'
import { CourseSelectionInfo } from './CourseSelectionInfo.frag'
import { NoCourse } from './NoCourse'
import type { RestaurantSearchCourse } from './RestaurantSearchCourse.frag'
import { CourseItem as PcCourseItem } from './pc/CourseComponent'
import { SeatOnlyCourse as PcSeatOnlyCourse } from './pc/SeatOnlyCourse'
import { CourseItem as SdCourseItem } from './sd/CourseComponent'
import { SeatOnlyCourse as SdSeatOnlyCourse } from './sd/SeatOnlyCourse'
import type { CourseItemProps } from './type'

type SeatOnlyPlan = ResultOf<typeof PlanSelectionInfo> & { fixedPoints: number }

type Props = {
  courses: ResultOf<typeof RestaurantSearchCourse>[]
  seatOnlyPlans: SeatOnlyPlan[]
  restaurantId: RestaurantId
  restaurant: CourseItemProps['restaurant']
  query: GuideQuery
  hideClear?: boolean
  defaultOpen?: boolean
}

export function CourseList({
  courses,
  seatOnlyPlans,
  restaurantId,
  restaurant,
  query,
  hideClear = false,
  defaultOpen = false,
}: Props) {
  const queryTab = useReserveOverlayContext('GUIDE')?.mealPeriod ?? 'DINNER'
  const { search } = useLocation()

  const sendClickEvent = useSendClickEvent()
  const navigate = useReserveNavigate()
  const isLoading = useIsLoading('GUIDE')

  const createTabTriggerHandler = useCallback(
    (mealPeriod: MealPeriodTab) => {
      return function (e: MouseEvent) {
        e.preventDefault()
        sendClickEvent({
          courseList: 'mealPeriod',
          click_event: 'newReservationFlow/courseList/mealPeriod',
        })
        navigate({ type: 'changeMealPeriod', mealPeriod })
      }
    },
    [navigate, sendClickEvent]
  )

  /*
    groupedCoursesは時間帯タブごとに通常コースのリストを持つ次の構造になっている。
    ただし、コースが存在しない時間帯は配列として持たない。

    [
      ['DINNER', [コース1, コース2, ...]],
      ['LUNCH', [コース3, コース4, ...]],
      ...
    ]

    また、groupedSeatOnlyPlansは、時間帯タブごとの席のみ予約プランのリストをgroupedCourseと同じ構造で持つ。
  */
  const groupedCourses = useMemo(() => groupCoursesByMealPeriodTab(courses), [courses])
  const groupedSeatOnlyPlans = useMemo(
    () => groupSeatOnlyPlansByMealPeriodTab(seatOnlyPlans),
    [seatOnlyPlans]
  )

  /*
    タブとして表示する時間帯、通常コースのリスト、席のみ予約プランのリストからなる、次のような構造を持つリスト。

    [
      { tab: 'DINNER', courses: [コース1, コース2, ...], seatOnlyPlans: [プラン1, ...] },
      { tab: 'LUNCH', courses: [コース3, コース4, ...], seatOnlyPlans: [] },
    ]
  */
  const shownTabs = useMemo(
    () =>
      mealPeriodTabs.flatMap((tab) => {
        /*
          coursesとseatOnlyPlansは、それぞれtabに対応する席のみ予約プランと通常コースのリスト。
          たとえば

          [
            ['DINNER', [コース1, コース2, ...]],
            ['LUNCH', [コース3, コース4, ...]]
          ]

          という構造でtabが'DINNER'なら[コース1, コース2, ...]を取得する。
        */
        const courses = groupedCourses.find(([t]) => t === tab)?.[1]
        const seatOnlyPlans = groupedSeatOnlyPlans.find(([t]) => t === tab)?.[1]

        return courses !== undefined || seatOnlyPlans !== undefined
          ? [{ tab, courses: courses ?? [], seatOnlyPlans: seatOnlyPlans ?? [] }]
          : []
      }),
    [groupedCourses, groupedSeatOnlyPlans]
  )

  useSetDefaultMealPeriodTab(restaurantId, shownTabs[0]?.tab)

  const activeTab =
    queryTab && shownTabs.some(({ tab }) => tab === queryTab)
      ? queryTab
      : shownTabs.length > 0
        ? shownTabs[0].tab
        : undefined // CourseSectionだけから使う限り、courseListかseatOnlyPlansのどちらかは存在するので、この行には到達しない

  const isPc = useIsPC()
  const SeatOnlyCourse = isPc ? PcSeatOnlyCourse : SdSeatOnlyCourse
  const onClickSeatOnlyCourse = useSeatOnlyCourseNavigation(seatOnlyPlans, activeTab)
  const CourseItem = isPc ? PcCourseItem : SdCourseItem

  const hasInventoryForAnyPlan = groupedCourses.length > 0
  const hasSeatOnlyPlan = seatOnlyPlans.length > 0
  const hasCriteria = useHasCriteria()
  if (!hasInventoryForAnyPlan && !hasSeatOnlyPlan) {
    return (
      <NoCourse
        mainText={hasCriteria ? NO_INVENTORY_CURRENT_CRITERIA : NO_INVENTORY_TEXT}
        restaurantId={restaurantId}
      />
    )
  }

  // mealPeriod 以外のクエリが存在する場合は条件クリアボタンを表示する
  const showConditionsClear = Object.keys(query).some(
    (key) => key !== 'mealPeriod' && query[key as keyof typeof query] !== undefined
  )

  const styles = sva({
    slots: ['container', 'tab', 'trigger', 'icon', 'content', 'note', 'nav'],
    base: {
      container: {
        spaceY: '5',
      },
      tab: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'between',
        px: '3',
      },
      trigger: {
        display: 'flex',
        flex: '1',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: 'medium',
        paddingBottom: '3',
        color: 'tertiary',
        '&:hover': {
          opacity: '75',
        },
        '&[data-state="active"]': {
          borderBottom: 'tab.active',
          borderColor: 'secondary',
          fontWeight: 'bold',
          color: 'gray.800',
        },
      },
      icon: {
        width: '[13px]',
        height: '[13px]',
        marginRight: '1',
        color: 'secondary',
      },
      content: {
        py: '5',
        spaceY: '2',
        _sd: {
          backgroundColor: 'gray.100',
          px: '3',
        },
      },
      note: {
        fontSize: 'xs',
        color: 'gray.600',
        _pc: {
          fontSize: 'sm',
        },
      },
    },
  })

  const classes = styles()

  return (
    <div className={classes.container}>
      <nav className={classes.nav}>
        <Tabs.Root value={activeTab}>
          <Tabs.List className={classes.tab}>
            {shownTabs.map(({ tab }) => {
              const Icon = tabIcons[tab]
              // FIXME: 次の state の url を取得するようにしたい
              const url = `/${restaurantId}/${tab.toLowerCase()}${search}`
              return (
                <Tabs.Trigger key={tab} value={tab} asChild>
                  <a href={url} className={classes.trigger} onClick={createTabTriggerHandler(tab)}>
                    <Icon className={classes.icon} />
                    {MEAL_PERIOD_LABEL[tab]}
                  </a>
                </Tabs.Trigger>
              )
            })}
          </Tabs.List>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              {shownTabs.map(({ tab, courses, seatOnlyPlans }) => (
                <Tabs.Content key={tab} value={tab} className={classes.content}>
                  {seatOnlyPlans.length > 0 && (
                    <SeatOnlyCourse
                      onClick={onClickSeatOnlyCourse}
                      onlyRequestReservations={seatOnlyPlans.every(
                        (plan) => plan.isRequestReservation
                      )}
                      fixedPoints={Math.max(
                        ...seatOnlyPlans.map((item) => {
                          return item.fixedPoints
                        })
                      )}
                      pageType="courseList"
                    />
                  )}
                  {courses.length > 0 && (
                    <>
                      {courses.map((item, index) => {
                        const course = getFragmentData(CourseSelectionInfo, item)
                        const plans = item.searchPlans.edges
                          .map((edge) => {
                            const price = getFragmentData(PlanSearchPrice, edge.node.searchPrice!)
                            return {
                              ...getFragmentData(PlanSelectionInfo, edge.node),
                              bestPrice: price.bestPrice,
                              priceVaried: price.priceVaried,
                              discountRate: price.discountPercent ?? 0,
                              pointRate: price.pointRatePercent,
                              fixedPoints: price.fixedPoints,
                            }
                          })
                          // 安い順に表示したいのでソートする
                          .sort((a, b) => a.bestPrice - b.bestPrice)
                        return (
                          <CourseItem
                            key={index}
                            course={course}
                            plans={plans}
                            restaurant={restaurant}
                            pageType="courseList"
                            defaultOpen={defaultOpen}
                          />
                        )
                      })}
                      <div className={classes.note}>
                        <p>
                          ※表示されている料金は最新の状況と異なる場合があります。予約情報入力画面にて合計金額をご確認ください。
                        </p>
                      </div>
                    </>
                  )}
                  {!hideClear && showConditionsClear && <ConditionsClear />}
                </Tabs.Content>
              ))}
            </>
          )}
        </Tabs.Root>
      </nav>
    </div>
  )
}

function Loading() {
  return (
    <>
      <div className="mt-3 flex flex-1 items-start justify-start space-x-3 rounded-md border border-neutral-200 bg-neutral-000 p-3">
        <Skeleton height={68} width={96} />
        <div className="flex flex-col ">
          <Skeleton height={13} width={219} />
          <Skeleton height={13} width={120} />
          <div className="mt-4 flex justify-between">
            <Skeleton height={13} width={84} />
            <Skeleton height={13} width={54} />
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-1 items-start justify-start space-x-3 rounded-md border border-neutral-200 bg-neutral-000 p-3">
        <Skeleton height={68} width={96} />
        <div className="flex flex-col ">
          <Skeleton height={13} width={219} />
          <Skeleton height={13} width={120} />
          <div className="mt-4 flex justify-between">
            <Skeleton height={13} width={84} />
            <Skeleton height={13} width={54} />
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-1 items-start justify-start space-x-3 rounded-md border border-neutral-200 bg-neutral-000 p-3">
        <Skeleton height={68} width={96} />
        <div className="flex flex-col ">
          <Skeleton height={13} width={219} />
          <Skeleton height={13} width={120} />
          <div className="mt-4 flex justify-between">
            <Skeleton height={13} width={84} />
            <Skeleton height={13} width={54} />
          </div>
        </div>
      </div>
    </>
  )
}
